import { computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, first, firstValueFrom, map, Observable, of, switchMap, timer } from 'rxjs';

import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    License,
    PeopleService,
    Project,
    ProjectControllerGetRequestParams,
    ProjectsService,
    Role,
    RolesService,
} from '@connect-services/src/sdk/api';
import { FormGroupType } from '@shared/client/models/form.model';
import { defaultPaginationParameters, OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';

export interface UserFormType extends FormGroupType {
    firstName: FormControl<string>;
    licenses: FormControl<License[]>;
    mail: FormControl<string>;
    name: FormControl<string>;
    projects: FormControl<Project[] | null>;
    role: FormControl<Role | null>;
    streetViewQuota: FormControl<number | null>;
}

const EMAIL_CHECK_DEBOUNCE = 1500;

export interface UserFormMethodsInterface {
    initForm(): FormGroup<UserFormType>;

    submitForm(): Promise<void>;
}

export abstract class AbstractUserFormComponent {
    public readonly allocatableQuota: WritableSignal<number | null> = signal(null);
    public readonly connectServicesLicense = computed(() => {
        return this.licensesSelected().find(
            (license: License) => license.application === License.ApplicationEnum.CONNECT_SERVICES,
        );
    });
    public readonly formGroup: WritableSignal<FormGroup<UserFormType> | null> = signal(null);
    public readonly hasRoleStreetViewPermission = signal(false);
    public readonly licensesSelected: WritableSignal<License[]> = signal([]);
    public readonly mailError: WritableSignal<string | undefined> = signal(undefined);
    public readonly rolesSelected: WritableSignal<SelectOption<Role>[]> = signal([]);
    public readonly streetViewQuotaDisabled = computed(() => {
        return (
            !this.connectServicesLicense() ||
            !this.rolesSelected()[0]?.value ||
            !this.securityService.meSignal()?.organization.streetViewQuotaRemaining
        );
    });
    public readonly streetViewQuotaTooltip: Signal<string> = computed(() => {
        if (!this.rolesSelected()?.length || !this.formGroup()?.controls.role.getRawValue()) {
            return 'noSelectedConnectServicesRoleTooltip';
        }

        if (this.securityService.meSignal()?.organization.streetViewQuota === null) {
            return 'streetView.organizationHasNoQuotaSet';
        }

        if (this.securityService.meSignal()?.organization.streetViewQuotaRemaining === 0) {
            return 'streetView.organizationHasNoQuotaLeft';
        }

        return '';
    });

    protected readonly peopleService = inject(PeopleService);
    protected readonly projectsService = inject(ProjectsService);
    protected readonly rolesService = inject(RolesService);
    protected readonly securityService = inject(SecurityService);
    protected readonly translocoService = inject(TranslocoService);

    private readonly fetchAllService = inject(FetchAllService);

    public checkMailExistsValidator(exceptions?: string[]): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            return timer(EMAIL_CHECK_DEBOUNCE).pipe(
                switchMap(() => {
                    const value = control.value;

                    if (!value || exceptions?.includes(value)) {
                        this.mailError.set(undefined);

                        return of(null);
                    }

                    return this.peopleService.personControllerGet({ email: value }).pipe(
                        first(),
                        map((personWithEmail) => {
                            const alreadyExists = personWithEmail.totalItems === 1;

                            if (alreadyExists) {
                                this.mailError.set('emailAlreadyUsedErrorMessage');

                                return { emailAlreadyExists: true };
                            }

                            this.mailError.set(undefined);

                            return null;
                        }),
                        catchError(() => of(null)),
                    );
                }),
            );
        };
    }

    public mailIsValid(formGroup: FormGroup, mailError: string | undefined): string | undefined {
        const mailControl: AbstractControl<string> = formGroup.controls['mail'];

        if (!mailControl || !mailControl.invalid || !mailControl.value) {
            return;
        }

        return mailError ?? this.translocoService.translate('emailInvalid');
    }

    public onLicensesChange(selectedLicenses: License[]): void {
        this.formGroup()!.controls.licenses.setValue(selectedLicenses);
        this.formGroup()!.controls.streetViewQuota.setValue(null);
        this.licensesSelected.set(selectedLicenses);

        this.formGroup()!.controls.role.clearValidators();

        this.formGroup()!.markAsDirty();

        if (!this.connectServicesLicense()) {
            this.formGroup()!.controls.role.setValue(null);
            this.formGroup()!.controls.role.updateValueAndValidity();
            this.rolesSelected.set([]);

            return;
        }

        this.formGroup()!.controls.role.addValidators(Validators.required);
        this.formGroup()!.controls.role.updateValueAndValidity();
    }

    public onSelectedProjectsChange(selectedProjects: Project[]): void {
        this.formGroup()!.controls.projects.setValue(selectedProjects);
    }

    public onSelectedRoleChange(selectedRole: Role[]): void {
        this.formGroup()!.controls.role.setValue(selectedRole[0]);
        this.formGroup()!.controls.streetViewQuota.setValue(null);

        if (!this.formGroup()!.controls.role.value) {
            this.rolesSelected.set([]);

            return;
        }

        const role = this.formGroup()!.controls.role.value!;
        this.rolesSelected.set([{ label: role.name, value: role }]);
    }

    protected fetchProjects(params?: ProjectControllerGetRequestParams): Promise<Project[]> {
        return this.fetchAllService.getAll<ProjectControllerGetRequestParams, Project>(
            this.projectsService.projectControllerGet.bind(this.projectsService),
            {
                ...params,
                isCaseSensitive: true,
                order: OrderEnum.ASC,
                orderBy: 'name',
            },
        );
    }

    protected async fetchRoles(): Promise<Role[]> {
        let hasNextPage = false;
        const paginationParameters = {
            ...defaultPaginationParameters,
            take: paginationMaxTake,
        };
        const roles: Role[] = [];

        do {
            const rolesPaginated = await firstValueFrom(this.rolesService.roleControllerGet(paginationParameters));
            roles.push(...rolesPaginated.items);
            paginationParameters.page! += 1;
            hasNextPage = rolesPaginated.hasNextPage;
        } while (hasNextPage);

        return roles;
    }
}
